// src/pages/errors/ErrorDashboardPage.jsx - Analytics Overview
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Hooks
import { useErrors } from '../hooks/useErrors';
import { useDepartments } from '../../departments/hooks/useDepartment';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

// Components
import ErrorDashboardHeader from '../components/ErrorDashboardHeader';
import ErrorSummaryCards from '../components/ErrorSummaryCards';
import ErrorAnalyticsCharts from '../components/ErrorAnalyticsCharts';
import ErrorQuickActions from '../components/ErrorQuickActions';

export default function ErrorDashboardPage() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // State for dashboard
  const [timeRange, setTimeRange] = useState(24); // hours
  const [selectedDepartment, setSelectedDepartment] = useState('');

  // API hooks
  const {
    errorSummary,
    loading,
    error,
    clearError,
    fetchErrorSummary,
    cleanupOldErrors,
  } = useErrors();

  const {
    departments,
    fetchAllDepartments,
  } = useDepartments();

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchErrorSummary({ hours: timeRange }),
        fetchAllDepartments({ include_deactivated: false }),
      ]);
    };

    loadInitialData();
  }, []);

  // Refresh data when filters change
  useEffect(() => {
    const params = {
      hours: timeRange,
      department_id: selectedDepartment || undefined,
    };

    fetchErrorSummary(params);
  }, [timeRange, selectedDepartment, fetchErrorSummary]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    const params = {
      hours: timeRange,
      department_id: selectedDepartment || undefined,
    };
    fetchErrorSummary(params);
  }, [timeRange, selectedDepartment, fetchErrorSummary]);

  const handleTimeRangeChange = useCallback((newRange) => {
    setTimeRange(newRange);
  }, []);

  const handleDepartmentChange = useCallback((newDepartment) => {
    setSelectedDepartment(newDepartment);
  }, []);

  const handleNavigateToBrowser = useCallback(() => {
    navigate('/errors/browser');
  }, [navigate]);

  const handleNavigateToEndpoint = useCallback((endpoint) => {
    navigate('/errors/browser', { 
      state: { 
        selectedEndpoint: endpoint,
        timeRange: timeRange 
      } 
    });
  }, [navigate, timeRange]);

  const handleNavigateToErrorCode = useCallback((errorCode) => {
    navigate('/errors/browser', { 
      state: { 
        selectedErrorCode: errorCode,
        timeRange: timeRange 
      } 
    });
  }, [navigate, timeRange]);

  const handleCleanupErrors = useCallback(() => {
    showConfirmDialog({
      title: 'Cleanup Old Errors',
      message: 'This will permanently delete error logs older than 30 days. This action cannot be undone.',
      confirmText: 'Cleanup',
      severity: 'warning',
      onConfirm: async () => {
        try {
          const result = await cleanupOldErrors({ days: 30 });
          if (result.success) {
            const serverResponse = result.data;
            const deletedCount = serverResponse.data?.deleted_count || 0;
            showSuccess(`Cleaned up ${deletedCount} old error logs`);
            handleRefresh();
          }
        } catch (err) {
          showError('Failed to cleanup old errors');
        }
      },
    });
  }, [showConfirmDialog, cleanupOldErrors, showSuccess, showError, handleRefresh]);

  return (
    <Box>
      {/* Header */}
      <ErrorDashboardHeader
        onRefresh={handleRefresh}
        onCleanup={handleCleanupErrors}
        onNavigateToBrowser={handleNavigateToBrowser}
        loading={loading}
        timeRange={timeRange}
        onTimeRangeChange={handleTimeRangeChange}
        selectedDepartment={selectedDepartment}
        onDepartmentChange={handleDepartmentChange}
        departments={departments}
        isDashboard={true}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <ErrorSummaryCards
        summary={errorSummary}
        loading={loading}
        timeRange={timeRange}
        onNavigateToEndpoint={handleNavigateToEndpoint}
        onNavigateToErrorCode={handleNavigateToErrorCode}
      />

      {/* Analytics Charts */}
      <ErrorAnalyticsCharts
        summary={errorSummary}
        loading={loading}
        onNavigateToEndpoint={handleNavigateToEndpoint}
        onNavigateToErrorCode={handleNavigateToErrorCode}
      />

      {/* Quick Actions */}
      <ErrorQuickActions
        summary={errorSummary}
        onNavigateToBrowser={handleNavigateToBrowser}
        onNavigateToEndpoint={handleNavigateToEndpoint}
        onNavigateToErrorCode={handleNavigateToErrorCode}
      />
      <ConfirmDialog />
    </Box>
  );
}